import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Container, Row, Button, Form, Table, Col, Alert } from 'react-bootstrap';
import Draggable from "react-draggable";
import RequestUtils from "../utils/requestUtils";
import siteUtils from "../utils/siteUtils";

const EarlyWarningScoresPopup = ({ data, onClose }) => {
	const { earlyWarningResponseObject, bankRequest, ewScore } = data;

	const sortedCalls = RequestUtils.getSortedEarlyWarningCalls(bankRequest);

	const getColorForMatchCode = (matchCode) => {
		switch (matchCode) {
			case 'Y':
				return 'green';
			case 'N':
				return 'red';
			case 'C':
				return '#9B7A01';
			case 'U':
				return 'gray';
			default:
				return 'black';
		}
	};


	const renderTable = () => {
		return (
			<Table striped bordered hover size="sm">
				<thead>
					<tr>
						<th style={{ width: "9%" }}>EW Score</th>
						<th style={{ width: "17%" }}>Date/Time</th>
						<th style={{ width: "73%" }}>Details Submitted</th>
					</tr>
				</thead>
				<tbody>
                    {sortedCalls.map((call, index) => {
                        const { earlyWarningRequest, earlyWarningRequestTypeId, earlyWarningResponse } = call;
                        const requestData = earlyWarningRequest?.EarlyWarningRequest || {};
                        let detailsSubmitted = [];

                        // Check if this is the last call in the sortedCalls array
                        const initialCall = index === sortedCalls.length - 1;

                        if (initialCall) {
                            // Handle the last call with only business details
                            const name = bankRequest.vendorRecord.name || "N/A";
                            const taxIdNumber = bankRequest.vendorRecord.taxIdNumber || "N/A";
                            const accountNumber = bankRequest.requestBank.bankAccountNumber || "N/A";
                            const routingNumber = bankRequest.requestBank.dfiNumber || "N/A";

                            detailsSubmitted = [
                                {
                                    text: `Name: ${name}`,
                                    color: getColorForMatchCode(
                                        earlyWarningResponse?.account_ownership?.match_results?.business?.name_match
                                    )
                                },
                                {
                                    text: `TIN: ${RequestUtils.maskTINString(taxIdNumber)}`,
                                    color: getColorForMatchCode(
                                        earlyWarningResponse?.account_ownership?.match_results?.business?.tax_id_number_match
                                    )
                                },
                                {
                                    text: `Account: ${accountNumber}`,
                                    color: getColorForMatchCode(
                                        earlyWarningResponse?.account_ownership?.match_results?.bank_account?.account_number_match
                                    )
                                },
                                {
                                    text: `Routing: ${routingNumber}`,
                                    color: getColorForMatchCode(
                                        earlyWarningResponse?.account_ownership?.match_results?.bank_account?.routing_number_match
                                    )
                                }
                            ];
                        } else {
                            // Handle other calls
                            const bankAccountDetails = requestData.bank_account
                                ? [
                                    {
                                        text: `Account: ${requestData.bank_account.account_number || ""}`,
                                        color: getColorForMatchCode(
                                            earlyWarningResponse?.account_ownership?.match_results?.bank_account?.account_number_match
                                        )
                                    },
                                    {
                                        text: `Routing: ${requestData.bank_account.routing_number || ""}`,
                                        color: getColorForMatchCode(
                                            earlyWarningResponse?.account_ownership?.match_results?.bank_account?.routing_number_match
                                        )
                                    }
                                ]
                                : [];

                            if (earlyWarningRequestTypeId === 1) {
                                const businessDetails = requestData.business
                                    ? [
                                        {
                                            text: `Name: ${requestData.business.name || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.business?.name_match
                                            )
                                        },
                                        {
                                            text: `TIN: ${RequestUtils.maskTINString(
                                                requestData.business.tax_id_number || ""
                                            )}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.business?.tax_id_number_match
                                            )
                                        }
                                    ]
                                    : [];

                                const addressDetails = requestData.address
                                    ? [
                                        {
                                            text: `Address Line 1: ${requestData.address.address_line_1 || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.address?.address_match
                                            )
                                        },
                                        {
                                            text: `Address Line 2: ${requestData.address.address_line_2 || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.address?.address_match
                                            )
                                        },
                                        {
                                            text: `City: ${requestData.address.city || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.address?.city_match
                                            )
                                        },
                                        {
                                            text: `State: ${requestData.address.state || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.address?.state_match
                                            )
                                        },
                                        {
                                            text: `Zip: ${requestData.address.zip || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.address?.zip_match
                                            )
                                        }
                                    ]
                                    : [];

                                detailsSubmitted = [...businessDetails, ...bankAccountDetails, ...addressDetails];
                            } else if (earlyWarningRequestTypeId === 2) {
                                const individualDetails = requestData.individual
                                    ? [
                                        {
                                            text: `First Name: ${requestData.individual.first_name || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.individual?.first_name_match
                                            )
                                        },
                                        {
                                            text: `Middle Name: ${requestData.individual.middle_name || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.individual?.middle_name_match
                                            )
                                        },
                                        {
                                            text: `Last Name: ${requestData.individual.last_name || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.individual?.last_name_match
                                            )
                                        },
                                        {
                                            text: `Name Suffix: ${requestData.individual.name_suffix || ""}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.individual?.name_suffix_match
                                            )
                                        },
                                        {
                                            text: `TIN: ${RequestUtils.maskTINString(
                                                requestData.individual.tax_id_number || ""
                                            )}`,
                                            color: getColorForMatchCode(
                                                earlyWarningResponse?.account_ownership?.match_results?.individual?.tax_id_number_match
                                            )
                                        }
                                    ]
                                    : [];

                                detailsSubmitted = [...individualDetails, ...bankAccountDetails];
                            }
                        }
						 
						return (
							<tr key={index}>
                                <td>{call.earlyWarningScore}</td>
                                <td>{call.timestamp && siteUtils.formatDate(call.timestamp, true)}</td>
								<td>
									{detailsSubmitted.map((detail, idx) => (
										<span key={idx} style={{ color: detail.color }}>
											{detail.text}
											{idx < detailsSubmitted.length - 1 && " | "}
										</span>
									))}
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>
		);
	};

	const renderTextFields = () => {
		return (
			<Row>
				<Col>
					<Form.Group controlId="textField1">
						<Form.Label style={{ fontWeight: "bold", color: "green" }}>Y = Match</Form.Label>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group controlId="textField2">
						<Form.Label style={{ fontWeight: "bold", color: "#FF0000" }}>N = No Match</Form.Label>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group controlId="textField3">
						<Form.Label style={{ fontWeight: "bold", color: "#9B7A01" }}>C = Partial Match</Form.Label>
					</Form.Group>
				</Col>
				<Col>
					<Form.Group controlId="textField4">
						<Form.Label style={{ fontWeight: "bold", color: "#808080" }}>U = Not Found</Form.Label>
					</Form.Group>
				</Col>
			</Row>
		);
	};
	return (
		<Draggable handle=".handle">
			<Modal centered={true} size="xl" isOpen={true} toggle={onClose} fade={false}>
				<ModalHeader toggle={onClose} className="handle early-warning-scores-header" close={<></>}>
					Early Warning API Calls
				</ModalHeader>
				<ModalBody>
					{renderTable()}
					{renderTextFields()}
				</ModalBody>
				<ModalFooter>
					<Button className="early-warning-scores-buttons" onClick={onClose}>
						Close
					</Button>
				</ModalFooter>
			</Modal>
		</Draggable>
	);
};

export default EarlyWarningScoresPopup;